import { memo, useCallback, useMemo } from 'react';

import { SerializedEditorState, SerializedLexicalNode } from 'lexical';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HeadingNode } from '@lexical/rich-text';
import { LinkNode } from '@lexical/link';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { ListItemNode, ListNode } from '@lexical/list';

import { SHARED_EDITOR_CONFIG, WysiwygConfigType } from '../input';
import { useLogger } from '../services/hooks';
import {
  CodeBlockNode,
  FileNode,
  ImagesGalleryNode,
  MentionNode,
  QuoteNode,
  ReferenceNode,
  SingleImageNode,
  VideoNode,
} from '../input/WysiwygInput/nodes';
import { AdditionalFetchDataHelpersPlugin } from '../input/WysiwygInput/plugins';
import { WysiwygContext } from '../input/WysiwygInput/context';

import { UpdateStatePlugin } from './plugins';

export const WysiwygContent = memo(
  ({
    editorStateJSON,
    apiUrl,
    envVars = { allowDirectVideoLinks: false },
    config = {
      plugins: {
        selectAllPlugin: { value: true, withDivider: true },
        formatHeadingPlugin: {
          heading: true,
          fontColor: true,
          backgroungColor: true,
          withDivider: true,
        },
        formatTextPlugin: {
          bold: true,
          italic: true,
          underline: true,
          strikethrough: true,
          code: true,
          superscript: true,
          subscript: true,
          withDivider: true,
        },
        listAndFormatPlugin: {
          align: true,
          list: true,
          indent: true,
          withDivider: true,
        },
        linkAndTablePlugin: {
          link: true,
          table: true,
          reference: true,
          codeBlock: true,
          quote: true,
          symbols: true,
          mention: true,
          withDivider: true,
        },
        mediaPlugin: {
          singeImage: true,
          imagesGallery: true,
          video: true,
          file: true,
          withDivider: true,
        },
        undoRedoPlugin: true,
        trackMediaIdsAndMentionsPlugin: true,
      },
      mode: 'default',
    },
    getAttachmentUrl,
  }: {
    apiUrl?: string;
    envVars?: {
      allowDirectVideoLinks: boolean;
    };
    editorStateJSON: SerializedEditorState<SerializedLexicalNode>;
    config?: WysiwygConfigType;
    getAttachmentUrl: (id: string) => Promise<string>;
  }) => {
    const logger = useLogger();
    const handleEditorError = useCallback(
      (editorError: Error) => {
        logger.error(editorError, {});
      },
      [logger],
    );

    const wysiwygContext = useMemo(
      () => ({
        apiUrl: apiUrl ?? '',
        envVars: {
          allowDirectVideoLinks: envVars.allowDirectVideoLinks ?? false,
        },
      }),
      [apiUrl, envVars.allowDirectVideoLinks],
    );

    const editorConfig = useMemo(
      () => ({
        ...SHARED_EDITOR_CONFIG,
        nodes: [
          ...(config.plugins.formatHeadingPlugin.heading ? [HeadingNode] : []),
          ...(config.plugins.linkAndTablePlugin.link ? [LinkNode] : []),
          ...(config.plugins.linkAndTablePlugin.table
            ? [TableNode, TableRowNode, TableCellNode]
            : []),
          ...(config.plugins.linkAndTablePlugin.mention ? [MentionNode] : []),
          ...(config.plugins.linkAndTablePlugin.reference
            ? [ReferenceNode]
            : []),
          ...(config.plugins.linkAndTablePlugin.codeBlock
            ? [CodeBlockNode]
            : []),
          ...(config.plugins.linkAndTablePlugin.quote ? [QuoteNode] : []),
          ...(config.plugins.listAndFormatPlugin.list
            ? [ListNode, ListItemNode]
            : []),
          ...(config.plugins.mediaPlugin.singeImage ? [SingleImageNode] : []),
          ...(config.plugins.mediaPlugin.imagesGallery
            ? [ImagesGalleryNode]
            : []),
          ...(config.plugins.mediaPlugin.video ? [VideoNode] : []),
          ...(config.plugins.mediaPlugin.file ? [FileNode] : []),
        ],
      }),
      [config.plugins],
    );

    return (
      <WysiwygContext.Provider value={wysiwygContext}>
        <LexicalComposer
          initialConfig={{
            ...{
              ...editorConfig,
              namespace: 'WysiwygContent',
            },
            editorState: (editor) => {
              const parsedState = editor.parseEditorState(editorStateJSON);
              editor.setEditorState(parsedState);
            },
            editable: false,
            onError: handleEditorError,
          }}
        >
          <AdditionalFetchDataHelpersPlugin getAttachmentUrl={getAttachmentUrl}>
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  readOnly
                  className="read-only-content"
                  aria-readonly="true"
                  contentEditable={false}
                />
              }
              placeholder={null}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <UpdateStatePlugin state={editorStateJSON} />
          </AdditionalFetchDataHelpersPlugin>
        </LexicalComposer>
      </WysiwygContext.Provider>
    );
  },
);
